import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { gt, not } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import ContentCard from '../../../app/content-card.gts';
import type IntlService from 'ember-intl/services/intl';
import screenIs from '../../../../helpers/screen-is.ts';
import { t } from 'ember-intl';
import type StoreService from '@ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type { ApprovedTotalLogSignature } from 'tio-data/models/tas-application';
import type { TransitionLogSignature } from '../../../../types/tuition-assistance.ts';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import {
  filterTransitionLogs,
  filterApprovedTotalLogs,
} from '../../../../utils/tuition-assistance/filter-logs.ts';

import Table from 'tio-reimagined/components/table';
import { Section } from 'tio-ui/components/layout';
import type Owner from '@ember/owner';
import { PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';

interface TuitionAssistanceProgramDetailsAppDetailsAppHistorySignature {
  Args: {
    application: TasApplicationModel | TasProgramInstanceModel;
    viewType: 'admin' | 'employee';
  };
  Element: HTMLElement;
}

type records = {
  when: string;
  nameAndRole: string;
  state: string;
  notes: string;
};

export default class TuitionAssistanceProgramDetailsAppDetailsAppHistoryComponent extends Component<TuitionAssistanceProgramDetailsAppDetailsAppHistorySignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked showAllRecords = false;
  @tracked transitionLog: TransitionLogSignature[] = [];
  @tracked approvedTotalLog: ApprovedTotalLogSignature[] = [];
  @tracked records: records[] = [];

  constructor(
    owner: Owner,
    args: TuitionAssistanceProgramDetailsAppDetailsAppHistorySignature['Args']
  ) {
    super(owner, args);
    this.transitionLog = filterTransitionLogs(
      this.args.application.transitionLog,
      this.modelNameState
    );
    // @ts-expect-error: approvedTotalLog property type not fully defined
    this.approvedTotalLog = filterApprovedTotalLogs(this.args.application.approvedTotalLog) || [];
    this.records = this.allLogsOrgranized();
  }

  displayState(stateString: string) {
    return stateString.split('.').slice(-1);
  }

  get modelNameState() {
    return this.args.application.modelName === 'tas-application'
      ? 'ApplicationState'
      : 'ProgramInstanceState';
  }

  get stateStringRoot() {
    return `common.status_message.to.TAS.${this.modelNameState}.`;
  }

  get logs() {
    const combinedLogs = [...this.transitionLog, ...this.approvedTotalLog];
    combinedLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return combinedLogs;
  }

  organizeTransitionLogData = (logs: TransitionLogSignature[]) => {
    if (!logs) {
      return [];
    }

    return logs.map((log) => {
      const { reason } = log;

      const name = reason?.by?.name || 'N/A';
      const relationshipType = reason?.by?.actingWithRole?.relationshipType;
      const role = relationshipType
        ? this.intl.t(`user-roles.${relationshipType.toLowerCase()}`)
        : 'N/A';

      const setNameAndRole = this.args.viewType === 'employee' ? role : `${name} - ${role}`;

      const nameAndRole = name === 'N/A' && role === 'N/A' ? 'System Event' : setNameAndRole;

      const stateStringToTranslate = `${this.stateStringRoot}${this.displayState(
        log.to.toUpperCase()
      )}`;
      return {
        when: log.timestamp,
        nameAndRole: nameAndRole,
        state: this.intl.t(stateStringToTranslate),
        notes: reason?.note || 'N/A',
      };
    });
  };

  organizeApprovedTotalLogData = (logs: ApprovedTotalLogSignature[]) => {
    if (!logs) {
      return [];
    }

    return logs.map((log) => {
      return {
        when: log.timestamp,
        nameAndRole: 'N/A',
        state: 'N/A',
        notes: `Approved Total was updated from ${this.formatMoney(log.from)} to ${this.formatMoney(
          log.to
        )}`,
      };
    });
  };

  allLogsOrgranized = () => {
    const transitionLogData = this.organizeTransitionLogData(this.transitionLog);
    const approvedTotalLogData = this.organizeApprovedTotalLogData(this.approvedTotalLog);

    const allLogs = [...transitionLogData, ...approvedTotalLogData];

    return allLogs.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
  };

  get headers() {
    return [
      {
        title: this.intl.t('tuition_assistance.program_details.application_history.when'),
        dataKey: 'when',
        formatter: 'formatWhen',
      },
      {
        title:
          this.args.viewType === 'admin'
            ? this.intl.t('tuition_assistance.program_details.application_history.name_and_role')
            : this.intl.t('tuition_assistance.program_details.application_history.role'),
        dataKey: 'nameAndRole',
      },
      {
        title: this.intl.t('tuition_assistance.program_details.application_history.state'),
        dataKey: 'state',
      },
      {
        title: this.intl.t('tuition_assistance.program_details.application_history.notes'),
        dataKey: 'notes',
      },
    ];
  }

  get allRecords() {
    const headers = this.headers;
    const results = this.records.map((record) => {
      const currentRecord = {};
      headers.forEach(({ title, dataKey, formatter }) => {
        // @ts-expect-error: not sure how to appease compiler here
        currentRecord[title] = formatter
          ? // @ts-expect-error: not sure how to appease compiler here
            this.formatWhen(record[dataKey])
          : // @ts-expect-error: not sure how to appease compiler here
            record[dataKey];
      });
      return currentRecord;
    });

    return this.showAllRecords ? results : results.slice(0, 1);
  }

  formatWhen = (dateString: string) => {
    const date = this.intl.formatDate(dateString);
    const time = this.intl.formatDate(dateString, {
      hour: 'numeric',
      minute: 'numeric',
    });

    return `${date} - ${time}`;
  };

  formatMoney = (cents: number | string) => {
    if (!Number(cents)) {
      return 'None';
    }

    const dollars = Number(cents) / 100;

    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  };

  <template>
    <Section id="app-history" ...attributes>
      <:header>
        {{t "tuition_assistance.program_details.application_history.default"}}
      </:header>
      <:body>
        {{#if (screenIs "md")}}
          <Table @isLoading={{false}}>
            <:thead as |options|>
              <options.tr>
                {{#each this.headers as |header|}}
                  <options.th>{{header.title}}</options.th>
                {{/each}}
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each this.allRecords as |record|}}
                <options.tr>
                  {{#each-in record as |_header value|}}
                    <options.td>{{htmlSafe value}}</options.td>
                  {{/each-in}}
                </options.tr>
              {{/each}}
            </:tbody>
          </Table>
        {{else}}
          {{#each this.allRecords as |record|}}
            <ContentCard class="mb-6" @title="">
              {{#each-in record as |header value|}}
                <dl class="flex gap-1">
                  <dt class="font-semibold py-1">
                    {{! @glint-expect-error: not sure how to type these}}
                    {{header}}:
                  </dt>
                  <dd class="py-1">
                    {{htmlSafe value}}
                  </dd>
                </dl>
              {{/each-in}}
            </ContentCard>
          {{/each}}
        {{/if}}
        {{#if (gt this.records.length 1)}}
          <div class="flex justify-center">
            <button
              type="button"
              class="font-semibold text-sm text-tio-gray-600"
              {{on "click" (fn (mut this.showAllRecords) (not this.showAllRecords))}}
            >
              {{#if this.showAllRecords}}
                {{t "tuition_assistance.program_details.application_history.view_less"}}
                <PhCaretDown @size="16" class="align-middle" />
              {{else}}
                {{t "tuition_assistance.program_details.application_history.view_more"}}
                <PhCaretUp @size="16" class="align-middle" />
              {{/if}}
            </button>
          </div>
        {{/if}}
      </:body>
    </Section>
  </template>
}
